import React, { useEffect, useState } from 'react'
import { Dialog } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { getIsGuideByKey, GUIDE_TYPE_ENUM, setIsGuideByKey } from 'utils/helper'
import { useSelector } from 'react-redux'
import { selectGuideListByKey } from 'stores/config/config.selector'
import styles from './styles.module.scss'

// ── Static video metadata — use i18n keys ─────────────────────────────────────
const VIDEO_META_KEYS = [
  { nameKey: 'introduce.video1_name', duration: '2:15', valueKey: 'introduce.video1_value', valueSubKey: 'introduce.video1_value_sub' },
  { nameKey: 'introduce.video2_name', duration: '3:40', valueKey: 'introduce.video2_value', valueSubKey: 'introduce.video2_value_sub' },
  { nameKey: 'introduce.video3_name', duration: '2:50', valueKey: 'introduce.video3_value', valueSubKey: 'introduce.video3_value_sub' },
]

// ── Support chat contacts — thay href bằng link thật ──────────────────────────
const CONTACTS = [
  {
    key: 'zalo',
    label: 'Chat Zalo',
    href: 'https://zalo.me/0877706134',
    bg: '#0068FF',
    icon: (
      <svg viewBox="0 0 20 20" fill="#fff" width="11" height="11">
        <text x="2" y="15" fontSize="14" fontWeight="800" fontFamily="sans-serif">Z</text>
      </svg>
    ),
  },
  {
    key: 'telegram',
    label: 'Chat Telegram',
    href: 'https://t.me/ntbaotran',
    bg: '#229ED9',
    icon: (
      <svg viewBox="0 0 24 24" fill="#fff" width="11" height="11">
        <path d="M21.9 4.6l-3.1 14.5c-.2 1-.8 1.3-1.7.8l-4.6-3.4-2.2 2.1c-.25.25-.45.45-.9.45l.3-4.6L18.1 7c.4-.3-.1-.5-.55-.2L7.2 13.2l-4.5-1.4c-1-.3-1-1 .2-1.5L20.6 3.2c.8-.3 1.5.2 1.3 1.4z"/>
      </svg>
    ),
  },
  {
    key: 'messenger',
    label: 'Chat Messenger',
    href: 'https://m.me/bombot.vn',
    bg: 'linear-gradient(45deg,#0099FF,#A033FF,#FF5280)',
    icon: (
      <svg viewBox="0 0 24 24" fill="#fff" width="11" height="11">
        <path d="M12 2C6.5 2 2 6.1 2 11.2c0 2.9 1.4 5.5 3.7 7.2V22l3.4-1.9c.9.3 1.9.4 2.9.4 5.5 0 10-4.1 10-9.2S17.5 2 12 2zm1.1 12.4l-2.6-2.7-5 2.7 5.5-5.8 2.6 2.7 4.9-2.7-5.4 5.8z"/>
      </svg>
    ),
  },
]

// ── Messages per step — use i18n keys ────────────────────────────────────────
const STEP_MESSAGE_KEYS = [
  'introduce.agent_msg1',
  'introduce.agent_msg2',
  'introduce.agent_msg3',
]

// ── Agent SVG placeholder — thay bằng <img src="..."> ảnh thật nếu có ─────────
const AgentAvatar = () => (
  <svg viewBox="0 0 52 52" style={{ width: 46, height: 46, borderRadius: '50%', display: 'block' }}>
    <circle cx="26" cy="26" r="26" fill="#DCFCE7"/>
    <circle cx="26" cy="21" r="9.5" fill="#FBD5B5"/>
    <path d="M16 20.5c0-6 4.5-10 10-10s10 4 10 10c0 1.2-2 1-2.6.2C32 17.5 29.5 15 26 15s-6 2.5-7.4 5.7c-.6.8-2.6 1-2.6-.2z" fill="#6B4423"/>
    <path d="M10 44c2-8 8.5-11.5 16-11.5S40 36 42 44l-32 0z" fill="#16A34A"/>
    <path d="M22 33.5c1.2 1 2.5 1.5 4 1.5s2.8-.5 4-1.5l-1 4h-6l-1-4z" fill="#fff"/>
    <rect x="13.5" y="19" width="3.4" height="7" rx="1.7" fill="#14532D"/>
    <rect x="35.1" y="19" width="3.4" height="7" rx="1.7" fill="#14532D"/>
    <path d="M15 20c0-7 5-12 11-12s11 5 11 12" stroke="#14532D" strokeWidth="2.2" fill="none"/>
    <path d="M37 26c0 3-2.5 5.5-6 6" stroke="#14532D" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
    <circle cx="30.5" cy="32.2" r="1.6" fill="#14532D"/>
    <circle cx="22.5" cy="21.5" r="1.2" fill="#3D2314"/>
    <circle cx="29.5" cy="21.5" r="1.2" fill="#3D2314"/>
    <path d="M24 25.5c1.3 1 2.7 1 4 0" stroke="#C4886A" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
  </svg>
)

export default function Introduce({ handleClose }) {
  const { t } = useTranslation()
  const [activeStep, setActiveStep]   = useState(0)
  const [done, setDone]               = useState([])   // indices of watched steps
  const [contacted, setContacted]     = useState(false)

  const introduceData = useSelector(selectGuideListByKey(GUIDE_TYPE_ENUM.video_introduce))
  const [tourConfig, setTourConfig]   = useState([])

  useEffect(() => {
    if (getIsGuideByKey(GUIDE_TYPE_ENUM.video_introduce) && introduceData?.length) {
      setTourConfig(introduceData)
    }
  }, [introduceData])

  const total = tourConfig.length || 0

  // Lấy meta tĩnh (tên chip + caption), fallback về data API nếu thiếu
  const getMeta = (i) => {
    const keys = VIDEO_META_KEYS[i]
    if (keys) {
      return {
        name: t(keys.nameKey),
        duration: keys.duration,
        value: t(keys.valueKey),
        valueSub: t(keys.valueSubKey),
      }
    }
    return {
      name: tourConfig[i]?.title || `Video ${i + 1}`,
      duration: null,
      value: tourConfig[i]?.title || '',
      valueSub: '',
    }
  }

  const closeModal = () => {
    setTourConfig([])
    setIsGuideByKey(GUIDE_TYPE_ENUM.video_introduce)
    handleClose()
  }

  const goStep = (i) => {
    if (i < 0 || i >= total) return
    setActiveStep(i)
  }

  const handleNext = () => {
    // Mark current step as done
    setDone((prev) => prev.includes(activeStep) ? prev : [...prev, activeStep])
    if (activeStep >= total - 1) { closeModal(); return }
    goStep(activeStep + 1)
  }

  const handleBack = () => goStep(activeStep - 1)

  const currentMsg = t(STEP_MESSAGE_KEYS[activeStep] || STEP_MESSAGE_KEYS[0])

  // ── Lấy videoId từ data API ────────────────────────────────────────────────
  const getVideoId = (item) => {
    if (!item?.link_video) return null
    // Hỗ trợ cả 2 dạng: full URL embed hoặc chỉ ID
    return item.link_video.replace('https://www.youtube.com/embed/', '').replace('https://youtu.be/', '').split('?')[0]
  }

  if (!tourConfig?.length) return null

  const currentItem = tourConfig[activeStep]
  const videoId     = getVideoId(currentItem)

  return (
    <Dialog
      maxWidth={false}
      open={tourConfig?.length > 0}
      PaperProps={{
        style: {
          width: '100%',
          maxWidth: 720,
          maxHeight: 'calc(100vh - 40px)',
          borderRadius: 16,
          background: '#fff',
          boxShadow: '0 24px 64px rgba(17,24,39,0.28)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          margin: 16,
        },
      }}
    >
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div style={{ padding: '16px 20px 0', flexShrink: 0 }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
            {t('introduce.title')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#16A34A', whiteSpace: 'nowrap' }}>
              {t('introduce.video_count', { done: done.length, total })}
            </span>
            <button
              title={t('introduce.close_tooltip')}
              onClick={closeModal}
              style={{
                width: 30, height: 30, border: 'none', background: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 6, color: '#9CA3AF',
              }}
            >
              <svg viewBox="0 0 14 14" fill="none" width="14" height="14">
                <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Step chips */}
        <div style={{ display: 'flex', gap: 6, marginTop: 12, overflowX: 'auto', paddingBottom: 2 }}>
          {tourConfig.map((item, i) => {
            const isDone   = done.includes(i)
            const isActive = activeStep === i
            const meta     = getMeta(i)
            return (
              <button
                key={i}
                onClick={() => goStep(i)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', flexShrink: 0,
                  border: `1px solid ${isActive ? '#22C55E' : '#E5E7EB'}`,
                  borderRadius: 999,
                  background: isActive ? '#F0FDF4' : '#fff',
                  color: isActive ? '#16A34A' : '#6B7280',
                  fontWeight: 600, fontSize: 12, cursor: 'pointer',
                  whiteSpace: 'nowrap', fontFamily: 'inherit',
                  transition: 'all .12s',
                }}
              >
                {/* Number badge */}
                <span style={{
                  width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
                  border: `1.5px solid ${isDone ? '#22C55E' : isActive ? '#22C55E' : '#D1D5DB'}`,
                  background: isDone ? '#22C55E' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9.5, fontWeight: 700, color: isDone ? '#fff' : isActive ? '#16A34A' : '#9CA3AF',
                  transition: 'all .15s',
                }}>
                  {isDone
                    ? <svg viewBox="0 0 12 12" fill="none" width="9" height="9"><path d="M2 6.5l2.5 2.5L10 3.5" stroke="#fff" strokeWidth="3.2" strokeLinecap="round"/></svg>
                    : i + 1
                  }
                </span>
                {meta.name}
                {meta.duration && (
                  <span style={{ fontSize: 10, fontWeight: 500, color: '#9CA3AF' }}>{meta.duration}</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── BODY ───────────────────────────────────────────────────────────── */}
      <div style={{ padding: '14px 20px 0', overflowY: 'auto', flex: 1, minHeight: 0 }}>

        {/* Video frame */}
        <div style={{
          position: 'relative', width: '100%', paddingTop: '56.25%', /* 16:9 */
          borderRadius: 12, overflow: 'hidden',
          background: 'linear-gradient(135deg,#14532D,#16A34A)',
        }}>
          {videoId ? (
            <iframe
              key={videoId}
              src={`https://www.youtube.com/embed/${videoId}?rel=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0,
              }}
              title={getMeta(activeStep).name}
            />
          ) : (
            /* Placeholder khi chưa có videoId */
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              {/* Brand top-left */}
              <div style={{
                position: 'absolute', left: 12, top: 11,
                display: 'flex', alignItems: 'center', gap: 7,
              }}>
                <div style={{
                  width: 25, height: 25, borderRadius: 7, background: 'rgba(255,255,255,0.92)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: 12, color: '#16A34A',
                }}>B</div>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.9)' }}>BOMBOT</span>
              </div>
              {/* Play button */}
              <div style={{
                width: 68, height: 68, borderRadius: '50%', background: 'rgba(255,255,255,.95)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(0,0,0,.25)',
              }}>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="#16A34A" style={{ marginLeft: 3 }}>
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
              {/* Duration badge */}
              {getMeta(activeStep).duration && (
                <div style={{
                  position: 'absolute', right: 10, bottom: 10, padding: '3px 8px',
                  borderRadius: 6, background: 'rgba(0,0,0,.6)', color: '#fff',
                  fontSize: 10.5, fontWeight: 600,
                }}>
                  {getMeta(activeStep).duration}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Caption — dùng VIDEO_META */}
        {(() => {
          const meta = getMeta(activeStep)
          return (
            <div style={{ fontSize: 13.5, fontWeight: 700, margin: '10px 2px 0', lineHeight: 1.45 }}>
              {meta.value}
              {meta.valueSub && (
                <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: '#6B7280', marginTop: 1 }}>
                  {meta.valueSub}
                </span>
              )}
            </div>
          )
        })()}

        {/* ── Support chat card ─────────────────────────────────────────────── */}
        <div style={{
          marginTop: 14, borderRadius: 12, padding: '14px 16px',
          background: 'linear-gradient(120deg,#14532D 0%,#16A34A 60%,#22C55E 100%)',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* decorative blob */}
          <div style={{
            position: 'absolute', right: -40, top: -60, width: 180, height: 180, borderRadius: '50%',
            background: 'radial-gradient(circle,rgba(255,255,255,.15),transparent 70%)',
            pointerEvents: 'none',
          }}/>

          <div style={{ display: 'flex', gap: 12, position: 'relative', zIndex: 1 }}>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ borderRadius: '50%', border: '2.5px solid rgba(255,255,255,.85)', lineHeight: 0 }}>
                <AgentAvatar />
              </div>
              {/* Online dot */}
              <span style={{
                position: 'absolute', right: 0, bottom: 10, width: 12, height: 12, borderRadius: '50%',
                background: '#4ADE80', border: '2.5px solid #14532D', display: 'block',
              }}/>
            </div>

            {/* Chat column */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Agent identity */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{t('introduce.agent_name')}</span>
                <span style={{
                  fontSize: 9.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase',
                  background: 'rgba(255,255,255,.18)', color: '#fff', padding: '2px 8px', borderRadius: 999,
                }}>{t('introduce.agent_role')}</span>
                <span style={{ fontSize: 10.5, color: '#BBF7D0', fontWeight: 600 }}>{t('introduce.agent_online')}</span>
              </div>

              {/* Speech bubble */}
              <div style={{
                position: 'relative', background: 'rgba(255,255,255,.97)', borderRadius: '4px 12px 12px 12px',
                padding: '9px 13px', fontSize: 12.5, lineHeight: 1.55, color: '#111827',
                boxShadow: '0 2px 8px rgba(0,0,0,.12)', maxWidth: 640,
              }}>
                {/* bubble tail */}
                <span style={{
                  position: 'absolute', left: -6, top: 0,
                  borderStyle: 'solid', borderWidth: '0 0 8px 7px',
                  borderColor: 'transparent transparent transparent rgba(255,255,255,.97)',
                  transform: 'scaleX(-1)',
                }}/>
                <span dangerouslySetInnerHTML={{ __html: currentMsg.replace(/<b>/g, '<b style="font-weight:700;color:#16A34A">') }} />
              </div>

              {/* CTA row — ẩn nếu đã contacted */}
              {!contacted && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.88)', whiteSpace: 'nowrap' }}>
                    {t('introduce.chat_direct')}
                  </span>
                  {CONTACTS.map((c) => (
                    <a
                      key={c.key}
                      href={c.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setContacted(true)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 12px 7px 8px', borderRadius: 999, border: 'none',
                        background: 'rgba(255,255,255,.96)', color: '#111827',
                        fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', textDecoration: 'none',
                        boxShadow: '0 2px 6px rgba(0,0,0,.14)', cursor: 'pointer',
                        transition: 'transform .12s, box-shadow .12s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,.22)' }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,.14)' }}
                    >
                      <span style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: c.bg,
                      }}>
                        {c.icon}
                      </span>
                      {c.label}
                    </a>
                  ))}
                </div>
              )}

              {contacted && (
                <div style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: '#FDE68A' }}>
                  {t('introduce.contacted')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* bottom spacing */}
        <div style={{ height: 14 }} />
      </div>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <div style={{
        padding: '12px 20px 16px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        borderTop: '1px solid #F3F4F6',
      }}>
        <button
          onClick={closeModal}
          style={{
            padding: '10px 8px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 500, color: '#9CA3AF', fontFamily: 'inherit',
          }}
        >
          {t('introduce.btn_skip')}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            disabled={activeStep === 0}
            onClick={handleBack}
            style={{
              padding: '10px 18px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
              cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', border: '1px solid #E5E7EB',
              background: '#fff', color: activeStep === 0 ? '#D1D5DB' : '#6B7280',
              opacity: activeStep === 0 ? 0.4 : 1, transition: 'opacity .12s',
            }}
          >
            {t('introduce.btn_back')}
          </button>
          <button
            onClick={handleNext}
            style={{
              padding: '10px 18px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', border: 'none',
              background: '#22C55E', color: '#fff',
              display: 'inline-flex', alignItems: 'center', gap: 7,
            }}
          >
            {activeStep >= total - 1 ? t('introduce.btn_finish') : t('introduce.btn_next')}
            <svg viewBox="0 0 14 14" fill="none" width="12" height="12">
              <path d="M3 7h8M7 3l4 4-4 4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </Dialog>
  )
}
